import type { SortingState, Table } from '@tanstack/react-table';

import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from 'lucide-react';
import { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface FsFiltersProps<T> {
  dataModel: Table<T>;
  sorting: SortingState;
}
export default function FsFilters<ModelType>(fsFiltersProps: FsFiltersProps<ModelType>) {
  const { dataModel, sorting } = fsFiltersProps;

  const sortingObject = sorting.length > 0 ? sorting[0] : undefined;

  const sortedColumn = sortingObject ? dataModel.getColumn(sortingObject.id) : undefined;

  const sortedLabel = sortedColumn?.columnDef.meta?.toString() ?? sortingObject?.id ?? 'Sortuj';

  const sortType = sortedColumn?.columnDef.sortingFn ?? 'string';

  const sortableColumns = useMemo(() => dataModel.getAllColumns().filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanSort()), [dataModel]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={'ghost'} className={'shrink-0'}>
          <span className={'bg-fill-brand-secondary rounded-sm p-1'}>
            {sortingObject ? (
              sortingObject.desc ? (
                <ArrowDownIcon aria-hidden={'true'} className={'stroke-fill-brand'} />
              ) : (
                <ArrowUpIcon aria-hidden={'true'} className={'stroke-fill-brand'} />
              )
            ) : (
              <ArrowUpDownIcon aria-hidden={'true'} className={'stroke-fill-brand'} />
            )}
          </span>
          {sortedLabel}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={'start'} className={'w-54'}>
        <DropdownMenuLabel>Sortuj według</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={sortingObject?.id}
          onValueChange={(val) => {
            const column = dataModel.getColumn(val);
            if (column) {
              const isDesc = sortingObject?.desc ?? false;
              column.toggleSorting(isDesc);
            }
          }}
        >
          {sortableColumns.map((column) => {
            return (
              <DropdownMenuRadioItem key={column.id} value={column.id}>
                {column.columnDef.meta?.toString() ?? column.id}
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Kolejność sortowania</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={sortingObject?.desc ? 'desc' : 'asc'}
          onValueChange={(val) => {
            if (sortedColumn) {
              sortedColumn.toggleSorting(val === 'desc');
            }
          }}
        >
          <DropdownMenuRadioItem value={'asc'} disabled={!sortedColumn}>
            {sortType === 'datetime' ? 'Od nowych do starych' : 'Od A do Z'}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value={'desc'} disabled={!sortedColumn}>
            {sortType === 'datetime' ? 'Od starych do nowych' : 'Od Z do A'}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
